import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Defaults must mirror lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG
const DEFAULTS = {
    filtroStatus: 'Vago/Disponível',
    limiteBusca: 15,
    maxSugestoes: 3,
    maxTentativasRelax: 3,
    rodadasParaLinkCompleto: 2,
};

// Clamp a numeric input to [min, max]; returns undefined when empty so the
// field keeps using the default from the agent runtime.
const coerceInt = (value, min, max) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.trunc(n);
    return Math.max(min, Math.min(max, i));
};

export default function BuscaCatalogoSection({ aiAgent, changeModuleConfig }) {
    const busca = aiAgent?.config?.module?.realstate?.busca || {};

    const updateBusca = (patch) => {
        changeModuleConfig({
            busca: {
                ...busca,
                ...patch,
            },
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:magnify" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Busca & Catálogo</CardTitle>
                        <CardDescription>Parâmetros operacionais da busca de imóveis e do fluxo de sugestões.</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>Status do imóvel para considerar disponível</Label>
                    <Input
                        value={busca.filtroStatus ?? ''}
                        onChange={(e) => updateBusca({ filtroStatus: e.target.value })}
                        placeholder={DEFAULTS.filtroStatus}
                        className={INPUT_CLS}
                    />
                    <p className={HINT_CLS}>Valor exato do campo <code>status</code> na coleção de imóveis. Padrão: <code>{DEFAULTS.filtroStatus}</code>.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Limite da busca no banco</Label>
                        <Input
                            type="number"
                            min={1}
                            max={100}
                            value={busca.limiteBusca ?? ''}
                            onChange={(e) => updateBusca({ limiteBusca: coerceInt(e.target.value, 1, 100) })}
                            placeholder={String(DEFAULTS.limiteBusca)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Máximo de imóveis retornados do MongoDB por query. Padrão: {DEFAULTS.limiteBusca}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Sugestões por resposta</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxSugestoes ?? ''}
                            onChange={(e) => updateBusca({ maxSugestoes: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxSugestoes)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Quantos imóveis a Lais apresenta ao cliente por rodada. Padrão: {DEFAULTS.maxSugestoes}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Tentativas de relaxamento</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxTentativasRelax ?? ''}
                            onChange={(e) => updateBusca({ maxTentativasRelax: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxTentativasRelax)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Quantas vezes a Lais tenta relaxar filtros antes de admitir que não achou nada. Padrão: {DEFAULTS.maxTentativasRelax}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Rodadas até link completo</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.rodadasParaLinkCompleto ?? ''}
                            onChange={(e) => updateBusca({ rodadasParaLinkCompleto: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.rodadasParaLinkCompleto)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Depois de N rodadas de sugestões, a Lais envia um link com todos os imóveis. Padrão: {DEFAULTS.rodadasParaLinkCompleto}.</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
